/**
 * pageSnaps.js - A modular scroll-snapping and sub-animation engine.
 * Specifically designed for Teratical's per-section interaction model.
 */
window.pageSnaps = (function ($) {
  let options = {
    sectionsSelector: ".outer-container > section",
    excludeLastSections: 0, // Number of sections at the end to leave out of snapping
    slideAnimations: [],
    snapDuration: 0.9,
    hasHero: true,
    hasFooterSnap: true,
  };

  let currentIndex = 0;
  let animating = false;
  let $snapSlides = null;
  let $allSections = null;
  let slideState = [];
  let numSnapItems = 0;
  let SNAP_DUR = 0.9;
  let SNAP_DUR_MS = 900;
  let licker = null;

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  function toSectionIndex(slideIdx) {
    return options.hasHero ? slideIdx - 1 : slideIdx;
  }



  function getSlideConfig(slideIdx) {
    const si = toSectionIndex(slideIdx);
    return si >= 0 && si < options.slideAnimations.length ? options.slideAnimations[si] : null;
  }

  function isSubSection(slideIdx) {
    const config = getSlideConfig(slideIdx);
    return !!(config?.subSlides || config?.steps || config?.triggerOnStart);
  }

  function lockForDuration(tl, fallback = 600) {
    animating = true;
    const hasDuration = tl && typeof tl.duration === "function" && tl.duration() > 0;
    if (hasDuration && typeof tl.eventCallback === "function") {
      const prev = tl.eventCallback("onComplete");
      tl.eventCallback("onComplete", () => {
        if (prev) prev();
        setTimeout(() => { animating = false; }, 100);
      });
    } else {
      setTimeout(() => { animating = false; }, fallback);
    }
  }

  function lockAndReverse(tl, fallback = 1500) {
    animating = true;
    if (!tl) {
      setTimeout(() => { animating = false; }, fallback);
      return;
    }
    let released = false;
    const release = () => {
      if (!released) {
        released = true;
        setTimeout(() => { animating = false; }, 100);
      }
    };
    const dur = (tl.duration() + 0.3) * 1000;
    setTimeout(release, Math.max(dur, fallback));
    const prev = tl.eventCallback("onReverseComplete");
    tl.eventCallback("onReverseComplete", () => {
      if (prev) prev();
      release();
    });
    tl.reverse();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // INTERACTION: SCRUBBING
  // ─────────────────────────────────────────────────────────────────────────

  function handleScrub(si, self) {
    const state = slideState[si];
    const config = options.slideAnimations[si];
    const stepIdx = state.subSlideIndex;
    const step = config.steps[stepIdx];
    const tl = state.stepTimelines[stepIdx];
    if (!tl) return;

    let sensitivity = 0.0015;
    const isTouch = self.event && (self.event.type.includes("touch") || self.event.pointerType === "touch");
    if (isTouch) sensitivity *= 3.5;

    if (step && step.scrubMultiplier) sensitivity *= step.scrubMultiplier;
    
    let newProgress = tl.progress() - self.deltaY * sensitivity;
    newProgress = Math.max(0, Math.min(1, newProgress));

    gsap.to(tl, {
      progress: newProgress,
      duration: 0.3,
      overwrite: "auto",
      ease: "power1.out",
    });

    if (config.onScrub) config.onScrub($snapSlides.eq(si)[0], newProgress);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TAPE MODE & STEP ENGINE
  // ─────────────────────────────────────────────────────────────────────────

  function setTapePosition(si, subIdx, animate = false, direction = "down") {
    const config = options.slideAnimations[si].subSlides;
    const sectionEl = $snapSlides.eq(si)[0];
    const wrapperEl = $(sectionEl).find(config.wrapperSelector)[0];
    const h = (typeof config.itemHeight === "function") ? config.itemHeight(sectionEl) : (config.itemHeight || sectionEl.offsetHeight);
    const y = -subIdx * h;
    const prevIdx = slideState[si].subSlideIndex;
    slideState[si].subSlideIndex = subIdx;

    if (!animate) {
      gsap.set(wrapperEl, { y });
      if (config.onSetTape) config.onSetTape(sectionEl, subIdx, prevIdx, animate, null, direction, slideState[si]);
      return null;
    }

    const tl = gsap.timeline();
    const tapeDelay = config.tapeDelay || 0;
    tl.to(wrapperEl, {
      y,
      duration: config.duration ?? SNAP_DUR,
      ease: config.ease ?? "power2.inOut",
    }, tapeDelay);

    if (config.onSetTape) config.onSetTape(sectionEl, subIdx, prevIdx, animate, tl, direction, slideState[si]);
    return tl;
  }

  function advanceAnimStep(si, direction) {
    const config = options.slideAnimations[si];
    const state = slideState[si];

    if (direction === "down") {
      const nextStepIdx = state.subSlideIndex + 1;
      const step = config.steps[nextStepIdx];
      const tl = state.stepTimelines[nextStepIdx] || null;
      state.subSlideIndex = nextStepIdx;

      if (step.scrub) {
        if (tl) tl.pause();
      } else {
        if (tl) {
          tl.play();
          lockForDuration(tl, SNAP_DUR_MS);
        }
      }
    } else {
      const currentStepIdx = state.subSlideIndex;
      const step = config.steps[currentStepIdx];
      const tl = state.stepTimelines[currentStepIdx] || null;
      state.subSlideIndex = currentStepIdx - 1;

      if (step.scrub) {
        // Just move index back
      } else {
        lockAndReverse(tl, SNAP_DUR_MS);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CORE NAVIGATION
  // ─────────────────────────────────────────────────────────────────────────

  function onEnterSlide(slideIdx, direction) {
    const si = toSectionIndex(slideIdx);
    if (si < 0 || si >= options.slideAnimations.length) return;
    const config = options.slideAnimations[si];
    if (!config) return;
    const state = slideState[si];
    const el = $snapSlides.eq(si)[0];

    // Initialization logic follows...

    if (config.steps && config.steps.length > 0) {
      const N = config.steps.length;
      if (direction === "up") {
        config.steps.forEach((step, i) => {
          const tl = step.build(el);
          if (tl) { tl.progress(1); tl.pause(); }
          state.stepTimelines[i] = tl;
        });
        state.subSlideIndex = N - 1;
      } else {
        state.stepTimelines.forEach((tl) => { if (tl) tl.kill(); });
        state.stepTimelines = new Array(N).fill(null);
        for (let i = N - 1; i >= 0; i--) {
          const step = config.steps[i];
          const tl = step.build(el);
          if (tl) { tl.progress(0); tl.pause(); }
          state.stepTimelines[i] = tl;
        }
        state.subSlideIndex = -1;

        if (config.steps[0] && config.steps[0].autoPlayDown) {
          const delay = config.steps[0].autoDownDelay ?? SNAP_DUR_MS;
          setTimeout(() => {
            if (currentIndex === slideIdx && state.subSlideIndex === -1 && !animating) {
              advanceAnimStep(si, "down");
            } else if (currentIndex === slideIdx && state.subSlideIndex === -1) {
              setTimeout(() => { if (currentIndex === slideIdx && state.subSlideIndex === -1) advanceAnimStep(si, "down"); }, 300);
            }
          }, delay);
        }
      }
      return;
    }

    if (config.subSlides) {
      const $subs = $(el).find(config.subSlides.selector);
      const startIdx = direction === "up" ? $subs.length - 1 : 0;
      if (config.subSlides.tape) {
        setTapePosition(si, startIdx, false, direction);
      }
      return;
    }
  }

  function onLeaveSlide(slideIdx, direction) {
    const si = toSectionIndex(slideIdx);
    if (si < 0 || si >= options.slideAnimations.length) return;
    const config = options.slideAnimations[si];
    if (!config) return;
    const state = slideState[si];
    const el = $snapSlides.eq(si)[0];

    if (config.onLeave) config.onLeave(el, direction, state);

    if (config.steps) {
      state.stepTimelines.forEach((tl) => { if (tl) tl.kill(); });
      state.stepTimelines = [];
      state.subSlideIndex = -1;
      return;
    }

    if (config.subSlides) {
      if (config.subSlides.tape) setTapePosition(si, 0, false);
      state.subSlideIndex = -1;
      return;
    }
  }

  function gotoIndex(index, customDuration) {
    if (animating || index < 0 || index >= numSnapItems) return;
    const previousIndex = currentIndex;
    const direction = index > previousIndex ? "down" : "up";
    const leavingSubSection = index !== previousIndex && isSubSection(previousIndex);

    if (index !== previousIndex && !leavingSubSection)
      onLeaveSlide(previousIndex, direction);

    animating = true;
    currentIndex = index;
    
    let targetY;
    if (options.hasHero && index === 0) {
      targetY = 0;
    } else if (toSectionIndex(index) < $snapSlides.length) {
      targetY = $snapSlides.eq(toSectionIndex(index)).offset().top;
    } else if (options.hasFooterSnap) {
      const $last = $snapSlides.eq($snapSlides.length - 1);
      targetY = $last.offset().top + $last.outerHeight();
    } else {
      animating = false;
      return;
    }

    gsap.to(window, {
      scrollTo: { y: targetY, autoKill: false },
      duration: customDuration !== undefined ? customDuration : SNAP_DUR,
      ease: "power2.inOut",
      overwrite: true,
      onStart: () => {
        if (isSubSection(index)) onEnterSlide(index, direction);
      },
      onComplete: () => {
        if (leavingSubSection) onLeaveSlide(previousIndex, direction);
        
        const config = getSlideConfig(index);
        const si = toSectionIndex(index);
        const state = slideState[si];
        const el = $snapSlides.eq(si)[0];

        if (!isSubSection(index)) onEnterSlide(index, direction);
        
        if (config?.onEnter && el) config.onEnter(el, direction, state);
        if (config?.onLocked && el) config.onLocked(el, direction, state);

        setTimeout(() => { animating = false; }, 200);
      },
    });
  }

  function init(config) {
    options = $.extend(true, {}, options, config);
    SNAP_DUR = options.snapDuration;
    SNAP_DUR_MS = SNAP_DUR * 1000;

    $(document).ready(function () {
      $allSections = $(options.sectionsSelector);
      if (!$allSections.length) return;
      
      $snapSlides = options.excludeLastSections > 0 
        ? $allSections.slice(0, -options.excludeLastSections) 
        : $allSections;

      gsap.registerPlugin(Observer, ScrollToPlugin, ScrollTrigger, SplitText);

      slideState = options.slideAnimations.map(() => ({
        hasPlayed: false,
        timeline: null,
        subSlideIndex: -1,
        stepTimelines: [],
      }));

      numSnapItems = $snapSlides.length;
      if (options.hasHero) numSnapItems += 1;
      if (options.hasFooterSnap) numSnapItems += 1;

      $snapSlides.each(function (i) {
        const config = options.slideAnimations[i];
        if (config && config.onInit) config.onInit(this);
      });

      licker = Observer.create({
        target: window,
        type: "wheel,touch,pointer",
        wheelSpeed: -1,
        tolerance: 10,
        preventDefault: true,
        onUp: (self) => { // Swiping up or wheel down -> intent to scroll DOWN the page (next section)
          if (animating) return;
          const config = getSlideConfig(currentIndex);
          const si = toSectionIndex(currentIndex);
          const state = slideState[si];

          // 1. Tape handling
          if (config?.subSlides?.tape) {
            const count = $($snapSlides.eq(si)[0]).find(config.subSlides.selector).length;
            if (state.subSlideIndex < count - 1) {
              const tl = setTapePosition(si, state.subSlideIndex + 1, true, "down");
              lockForDuration(tl, SNAP_DUR_MS);
              return;
            }
          }

          // 2. Step handling
          if (config?.steps && config.steps.length > 0) {
            const currentStep = config.steps[state.subSlideIndex];
            if (currentStep?.scrub) {
              const tl = state.stepTimelines[state.subSlideIndex];
              if (tl && tl.progress() < 0.999) { handleScrub(si, self); return; }
            }
            if (state.subSlideIndex < config.steps.length - 1) { advanceAnimStep(si, "down"); return; }
          }

          if (currentIndex < numSnapItems - 1) gotoIndex(currentIndex + 1);
          else if (options.excludeLastSections > 0) {
            licker.disable();
            document.body.classList.remove("is-snapping");
          }
        },
        onDown: (self) => { // Swiping down or wheel up -> intent to scroll UP the page (prev section)
          if (animating) return;
          const config = getSlideConfig(currentIndex);
          const si = toSectionIndex(currentIndex);
          const state = slideState[si];

          // 1. Tape handling
          if (config?.subSlides?.tape) {
            if (state.subSlideIndex > 0) {
              const tl = setTapePosition(si, state.subSlideIndex - 1, true, "up");
              lockForDuration(tl, SNAP_DUR_MS);
              return;
            }
          }

          // 2. Step handling
          if (config?.steps && config.steps.length > 0) {
            const currentStep = config.steps[state.subSlideIndex];
            if (currentStep?.scrub) {
              const tl = state.stepTimelines[state.subSlideIndex];
              if (tl && tl.progress() > 0.001) { handleScrub(si, self); return; }
            }
            if (state.subSlideIndex > -1) {
              const currentStepCopy = currentStep;
              advanceAnimStep(si, "up");
              if (currentStepCopy?.autoUpJump && currentIndex > 0) {
                const tlForStep = state.stepTimelines[state.subSlideIndex + 1];
                const waitDur = tlForStep && typeof tlForStep.duration === "function" ? tlForStep.duration() * 1000 + 200 : 1000;
                setTimeout(() => { animating = false; gotoIndex(currentIndex - 1); }, waitDur);
              }
              return;
            }
          }

          if (currentIndex > 0) gotoIndex(currentIndex - 1);
        },
      });
      
      document.body.classList.add("is-snapping");

      if (options.excludeLastSections > 0) {
        ScrollTrigger.create({
          trigger: $allSections.last(),
          start: "top 85%",
          onLeaveBack: () => {
            if (!licker.isEnabled) {
              licker.enable();
              document.body.classList.add("is-snapping");
              currentIndex = numSnapItems - 1;
              gotoIndex(currentIndex, 0.25);
            }
          },
          onEnter: () => { 
            if (licker && licker.isEnabled) {
              let tapeNotFinished = false;
              const config = getSlideConfig(currentIndex);
              if (config && config.subSlides && config.subSlides.tape) {
                const si = toSectionIndex(currentIndex);
                const state = slideState[si];
                if (state) {
                  const count = $($snapSlides.eq(si)[0]).find(config.subSlides.selector).length;
                  if (state.subSlideIndex < count - 1) tapeNotFinished = true;
                }
              }
              if (!tapeNotFinished) {
                licker.disable();
                document.body.classList.remove("is-snapping");
              }
            }
          },
        });
      }

      // Initial Scroll Handling
      let initialIndex = 0;
      const hash = window.location.hash;
      const currentScroll = window.scrollY || document.documentElement.scrollTop;

      if (hash) {
        let $target = $(hash);
        if (!$target.length) $target = $("." + hash.replace("#", ""));
        if ($target.length) {
          const idx = $snapSlides.index($target);
          if (idx >= 0) initialIndex = options.hasHero ? idx + 1 : idx;
        }
      } else if (currentScroll > 10) {
        let minDiff = Math.abs(0 - currentScroll);
        $snapSlides.each(function (i, section) {
          const targetY = $(section).offset().top;
          const diff = Math.abs(targetY - currentScroll);
          if (diff < minDiff) { minDiff = diff; initialIndex = options.hasHero ? i + 1 : i; }
        });
      }

      if (initialIndex >= 0) {
        currentIndex = initialIndex;
        let targetY = currentIndex === 0 ? 0 : $snapSlides.eq(toSectionIndex(currentIndex)).offset().top;
        gsap.set(window, { scrollTo: { y: targetY, autoKill: false } });
      }

      onEnterSlide(currentIndex, "down");
      const initialConfig = getSlideConfig(currentIndex);
      if (initialConfig?.onEnter) {
        const si = toSectionIndex(currentIndex);
        initialConfig.onEnter($snapSlides.eq(si)[0], "down", slideState[si]);
      }
    });
  }

  return { init, gotoIndex };
})(jQuery);
